import { useState, useEffect } from "react";
import { 
  LineChart, Line, PieChart, Pie, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend
} from "recharts";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GlobalMarketData() {
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/market-data`);
        setMarketData(response.data);
      } catch (error) {
        console.error("Error fetching market data:", error);
        // Fallback data
        setMarketData({
          carbon_prices: [
            { month: "Jan", eu_ets: 82, uk_ets: 75, china: 8 },
            { month: "Feb", eu_ets: 85, uk_ets: 78, china: 8.5 },
            { month: "Mar", eu_ets: 88, uk_ets: 80, china: 9 },
            { month: "Apr", eu_ets: 90, uk_ets: 82, china: 9.2 },
            { month: "May", eu_ets: 92, uk_ets: 85, china: 9.5 },
            { month: "Jun", eu_ets: 95, uk_ets: 88, china: 10 },
          ],
          regional_markets: [
            { name: "EU ETS", value: 45, color: "#1A4D2E" },
            { name: "China ETS", value: 25, color: "#2E5A88" },
            { name: "North America", value: 15, color: "#10B981" },
            { name: "UK ETS", value: 8, color: "#6366F1" },
            { name: "Others", value: 7, color: "#F59E0B" },
          ],
          market_growth: [
            { year: "2020", value: 280 },
            { year: "2021", value: 380 },
            { year: "2022", value: 520 },
            { year: "2023", value: 680 },
            { year: "2024", value: 850 },
            { year: "2025", value: 950 },
          ]
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading || !marketData) {
    return (
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-10 bg-slate-200 rounded w-1/3 mx-auto" />
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-64 bg-slate-100 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-white" data-testid="global-market-data">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="font-montserrat text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Global Carbon Market Data
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Real-time insights into global carbon markets and pricing trends.
          </p>
        </div>

        {/* Charts Grid */}
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Line Chart - Carbon Prices */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-card" data-testid="carbon-price-chart">
            <h3 className="font-montserrat font-semibold text-slate-900 mb-4">
              Carbon Prices (€/tonne)
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={marketData.carbon_prices}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748B' }} />
                <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line type="monotone" dataKey="eu_ets" stroke="#1A4D2E" strokeWidth={2} dot={{ fill: '#1A4D2E' }} name="EU ETS" />
                <Line type="monotone" dataKey="uk_ets" stroke="#2E5A88" strokeWidth={2} dot={{ fill: '#2E5A88' }} name="UK ETS" />
                <Line type="monotone" dataKey="china" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981' }} name="China ETS" />
                <Legend />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Pie Chart - Regional Markets */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-card" data-testid="regional-markets-chart">
            <h3 className="font-montserrat font-semibold text-slate-900 mb-4">
              Regional Market Share
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={marketData.regional_markets}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {marketData.regional_markets.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Bar Chart - Market Growth */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-card" data-testid="market-growth-chart">
            <h3 className="font-montserrat font-semibold text-slate-900 mb-4">
              Market Volume ($B)
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={marketData.market_growth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#64748B' }} />
                <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="value" fill="#1A4D2E" radius={[4, 4, 0, 0]} name="Market Value ($B)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </section>
  );
}
